"use client";

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { creditApi, orderApi } from "src/services/api";
import {
  GetOrderDto,
  UpdateOrderCreditDto,
} from "src/services/generated-api";
import { useOrder } from "@contexts/index";
import { orderQueryKey, useOrderQuery } from "./useOrder";
import {
  appliedCreditCents,
  hasCreditApplied,
  preCreditTotalCents,
} from "src/utils/credit-amount";
import {
  creditApplyProblem,
  creditEditable,
  creditLockedByPayment,
} from "src/utils/credit-offer";
import { markAutoApplied, setOptedOut } from "src/utils/credit-choice";
import {
  creditOperations,
  redeemAndApply,
  requestTopUp,
} from "src/utils/credit-operations";
import { normalizeGiftCardCode } from "src/utils/gift-card";
import {
  CreditMinimumLine,
  creditMinimumLines,
  usableNowCents,
} from "src/utils/credit-minimum";
import { giftCardRedeemErrorCode } from "src/utils/gift-card-redeem";
import { CreditLotView, applyAfterRedeem, freshQuery } from "src/utils/credit-lots";
import {
  accountEmail as readAccountEmail,
  isSignedIn as readSignedIn,
  sessionKey as readSessionKey,
  subscribeToSignIn,
} from "src/utils/signed-in";
import {
  CreditProblem,
  creditProblemFromApiError,
  creditTokenRefused,
} from "src/utils/credit-problem";
import {
  clearStoredCreditToken,
  creditTokenScope,
  readStoredCreditToken,
  readUsableCreditToken,
  redeemCodeDto,
  storeRedeemedCreditToken,
  subscribeToCreditToken,
} from "src/utils/credit-token";
import { trackLoss, trackStep } from "src/utils/funnel";

/**
 * Applying and removing customer credit, in ONE place.
 *
 * The coupon equivalent of this is pasted into `delivery.tsx` and
 * `cart-drawer/index.tsx`, and the two copies have already drifted: the drawer
 * dropped the try/catch, derives its currency differently, and re-fetches the
 * order WITHOUT the guest token — which silently 401s for a guest, so the
 * drawer's cache write is a no-op for exactly the customers who need it. There
 * is no third copy of that mistake here; both hosts render one `CreditSection`,
 * which calls this hook, which is the only code that touches the endpoints.
 *
 * The mutation sequence is the canonical one from `delivery.tsx`'s
 * `applyCoupon`, and all three round-trips are mandatory:
 *
 *   1. apply / remove  — returns `void`, genuinely; nothing comes back
 *   2. calculateAmount — re-prices the order now that credit moved
 *   3. getOne + setQueryData — the only way to learn the new figures
 *
 * `x-client-channel` stays `undefined` throughout. The server derives the
 * channel from the domain, and passing one re-derives the order's channel and
 * switches the pricing chain (see the x-client-channel note in
 * src/api/order.api.ts `createOrder`) — on an endpoint
 * that redeems money, that would re-quote the redemption on the wrong chain.
 *
 * Note the argument order differs between the two generated calls: the DTO is
 * second on apply, the guest token is second on remove.
 */

type UseOrderCredit = {
  /** Credit already redeemed against this order, in minor units. 0 is real. */
  creditAppliedCents: number;
  /** Presence, never truthiness — an order whose credit was removed reads 0. */
  hasCredit: boolean;
  /** A live claim token is held, so there is credit to offer. */
  hasClaimToken: boolean;
  /**
   * A live claim token is stored but the order has no email yet, so it cannot
   * be matched (and is not sent). Its credit is unknown until the delivery
   * step, which is why the drawer's cashback reads "do X €" (NEW-2).
   */
  tokenAwaitingEmail: boolean;
  /**
   * Which kind: "lot" is the token from typed codes, whose balance is those
   * cards' alone; "address" is the emailed claim link's.
   */
  tokenScope: "lot" | "address" | null;
  /**
   * This browser carries a signed-in session, which is the OTHER credential
   * `proveCreditOwner` accepts — with no token at all. See `credit-offer.ts`.
   */
  isSignedIn: boolean;
  currency: string;
  /**
   * What this customer can spend ON THIS ORDER, in minor units: the balance
   * `GET /credit/balance` reports, minus whatever a lot's order minimum holds
   * back (`minimumLines`). Null while unknown (loading, failed, or no
   * credential to ask with). Null is never zero — see `creditOffer`.
   */
  balanceCents: number | null;
  /**
   * Credit this order cannot use yet only because its goods are below a
   * lot's minimum, one line per minimum. Empty when there is none, or when
   * the server did not say.
   */
  minimumLines: CreditMinimumLine[];
  /**
   * The customer's lots as the server judged them for this order (the lot
   * picker). Null while unknown — loading, failed, or an older server that
   * does not list them — and never confused with [] ("no credit at all").
   */
  lots: CreditLotView[] | null;
  /** AUTO: soonest-expiring first; MANUAL: the customer chose. Null when not said. */
  selectionMode: "AUTO" | "MANUAL" | null;
  /**
   * The balance read failed or was refused — NOT still loading. The offer
   * must then not claim credit exists (see `creditControl`).
   */
  balanceFailed: boolean;
  /**
   * The balance was refused with CREDIT_ACCOUNT_UNVERIFIED: signed in to an
   * account whose email was never confirmed, with no token. See `creditControl`.
   */
  accountUnverified: boolean;
  /**
   * The order can still change. False once it is placed or paid — the credit
   * box must then offer nothing (see `creditControl`).
   */
  orderEditable: boolean;
  /**
   * Not editable because a card payment has begun and is unfinished (Back
   * from Stripe, or declined): the server's pin, which only it can release.
   * The box says so instead of silently freezing (`creditLockedByPayment`).
   */
  paymentStarted: boolean;
  /** The order this box is about; keys the customer's per-order choices. */
  orderId: string | null;
  /**
   * A credit operation is running or queued for this order, or payment has
   * begun — in ANY mount of the checkout (`credit-operations.ts`). Every
   * credit control waits while it is true.
   */
  busy: boolean;
  /** Set when the last attempt was refused; already a customer-facing problem. */
  problem: CreditProblem | null;
  clearProblem: () => void;
  /**
   * The customer ticked (true) or unticked (false) "use my credit": an
   * explicit choice, remembered for this order and queued behind whatever
   * credit operation is already running.
   */
  choose: (use: boolean) => Promise<void>;
  /**
   * The customer ticked or unticked lots in the picker: `lotIds` is the whole
   * new choice ([] = none, which takes the credit off). Queued and recorded
   * like `choose`.
   */
  chooseLots: (lotIds: string[]) => Promise<void>;
  /** The checkout's own once-per-order apply; dropped by any newer choice. */
  autoApply: () => Promise<void>;
  /**
   * "Imam darilni bon": trade a gift-card code for a claim token, store it,
   * and put the money on the order, as ONE queued operation.
   *
   * @returns whether it was redeemed, once the apply after it has finished
   *          too — the caller only needs to know whether to collapse its field.
   */
  redeemCode: (code: string) => Promise<boolean>;
  /** The last redeem attempt's `ApiError.code` (or `"RATE_LIMITED"`); see `giftCardRedeemMessage`. */
  redeemErrorCode: string | null;
  clearRedeemError: () => void;
};

export function useOrderCredit(): UseOrderCredit {
  const { mainOrderId, guestToken } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const queryClient = useQueryClient();

  const [problem, setProblem] = useState<CreditProblem | null>(null);
  const [redeemErrorCode, setRedeemErrorCode] = useState<string | null>(null);

  /**
   * Who a held token may be sent for (`credit-token.ts`): this order's
   * shipping email and the signed-in account's. A token for anybody else —
   * left behind by a previous session, or a guest whose order email changed
   * since — is never read below, so it is never sent.
   */
  const accountEmail = useSyncExternalStore(subscribeToSignIn, readAccountEmail, () => null);
  const orderEmail = order?.shippingEmail ?? null;
  const audience = useMemo(
    () => ({ orderEmail, accountEmail }),
    [orderEmail, accountEmail],
  );

  /**
   * Whether a live claim token is held, and may be sent for this order.
   *
   * `useSyncExternalStore`, because localStorage IS an external store and this
   * is the only way to read one without either a hydration mismatch or a
   * mount-order race:
   *
   *  - the server snapshot is hard-coded false. These are client components but
   *    Next still renders them on the server, where there is no localStorage;
   *    reading it inline would make the server render "no credit" and the first
   *    client render "credit".
   *  - copying it into state from an effect instead would reintroduce the race
   *    it replaced: `CreditClaimBanner` WRITES the token from its own effect,
   *    and effects fire children-first, so a section mounted beneath it on the
   *    landing page could read before the banner had written and then never
   *    look again.
   *
   * It also means the drawer's copy and the delivery step's copy — which can be
   * mounted at once — agree the instant the token is stored or dropped.
   */
  const hasClaimToken = useSyncExternalStore(
    subscribeToCreditToken,
    () => readUsableCreditToken(audience) !== null,
    () => false,
  );

  const tokenAwaitingEmail = useSyncExternalStore(
    subscribeToCreditToken,
    () => !(orderEmail ?? "").trim() && readStoredCreditToken() !== null,
    () => false,
  );

  const tokenScope = useSyncExternalStore(
    subscribeToCreditToken,
    () => creditTokenScope(audience),
    () => null,
  );

  /**
   * The second credential. `proveCreditOwner` accepts a signed-in owner with no
   * token, so a customer whose two-hour token lapsed can still spend their own
   * credit — which is exactly what this hook already posts `{}` for.
   *
   * Same `useSyncExternalStore` shape as the token, and for the same hydration
   * reason: these are client components but Next still renders them on the
   * server, where there is no `document.cookie`, so the server snapshot is
   * hard-coded false and the first client render is what decides.
   */
  const isSignedIn = useSyncExternalStore(
    subscribeToSignIn,
    readSignedIn,
    () => false,
  );
  /** Which session, so one account's balance is never served to the next. */
  const sessionKey = useSyncExternalStore(
    subscribeToSignIn,
    readSessionKey,
    () => null,
  );

  /**
   * Re-read the order from the server and publish it to the cache.
   *
   * WITH the guest token, unlike the drawer's coupon copy. `useOrder`'s
   * `refetchOnMount: "always"` would eventually repair a missed write, but not
   * before the customer has looked at a stale total on the screen where they
   * decide whether to pay.
   */
  const refreshOrder = useCallback(
    async (orderId: string): Promise<GetOrderDto | null> => {
      const token = guestToken ?? undefined;
      await orderApi.orderControllerCalculateAmount(orderId, token);
      const response = await orderApi.getOneBaseOrderControllerOrder(
        orderId,
        token,
      );
      const updated = response.data as GetOrderDto;
      if (updated?.id) {
        queryClient.setQueryData(orderQueryKey(updated.id), updated);
      }
      // Returned as well as cached: the caller needs the figures NOW, and the
      // cache write only reaches this component on the next render.
      return updated ?? null;
    },
    [guestToken, queryClient],
  );

  /**
   * The balance, asked with whichever credential the customer holds — the same
   * two `proveOwner` accepts. Never asked without one: the endpoint would only
   * refuse, and a guest with no token has no credit this page could offer.
   *
   * Keyed on what is applied, so applying or removing credit re-asks and the
   * box can show what is left after this order. The token itself is
   * read inside the fetch and kept OUT of the query key, so it never sits in
   * the query cache. A refusal or a network error leaves this null, and
   * `creditControl` then falls back to its pre-balance behaviour, minus the
   * "confirmed" wording (`balanceFailed`).
   *
   * A refusal that condemns the token (expired, invalid, another address's)
   * also drops it. Left in storage it would be sent for up to two hours,
   * blocking a signed-in customer's own credit; dropping it changes
   * `hasClaimToken`, which re-keys this query to ask with the account
   * instead. Spent or lapsed does NOT drop it: the backend now answers a
   * spent or held card's token with a balance of 0 rather than CREDIT_SPENT,
   * and if an older one still refuses, the token remains the proof of the
   * cards it names (`creditTokenRefused`).
   */
  const orderCurrency = order?.currency || "";
  const credentialHeld = hasClaimToken || isSignedIn;
  /**
   * The balance query's key for an order as it stands. A function, so the
   * queued apply can warm the cache for the order it has just re-read
   * (`refreshBalance`) under exactly the key this hook will ask next.
   */
  const balanceKey = useCallback(
    (o: GetOrderDto | undefined | null) => [
      "credit-balance",
      o?.currency || "",
      hasClaimToken,
      isSignedIn,
      sessionKey,
      appliedCreditCents(o),
      o?.id,
      // A cart edit moves the goods a minimum is judged on.
      preCreditTotalCents(o),
    ],
    [hasClaimToken, isSignedIn, sessionKey],
  );
  const fetchBalance = useCallback(
    async (currency: string, orderId: string | undefined) => {
      const token = readUsableCreditToken(audience) ?? undefined;
      try {
        // The cart on screen: credit held on the customer's OTHER open carts
        // (auto-applied on one they abandoned) then counts as available,
        // and applying moves it over.
        const { data } = await creditApi.creditControllerBalance(
          currency,
          token,
          orderId,
        );
        return {
          balanceCents: data.balanceCents,
          lockedByMinimum: data.lockedByMinimum,
          orderGoodsCents: data.orderGoodsCents,
          lots: Array.isArray(data.lots) ? (data.lots as CreditLotView[]) : null,
          selectionMode: data.selectionMode ?? null,
        };
      } catch (error: unknown) {
        if (token && creditTokenRefused(creditProblemFromApiError(error))) {
          clearStoredCreditToken(token);
        }
        throw error;
      }
    },
    [audience],
  );
  const balanceQuery = useQuery({
    queryKey: balanceKey(order),
    // Asked while credit IS applied too: the box then shows what is left.
    enabled: credentialHeld && orderCurrency !== "",
    queryFn: () => fetchBalance(orderCurrency, order?.id),
    // A 400 (expired token, no proof) will not improve by asking again.
    retry: false,
    staleTime: 30_000,
  });

  /**
   * Re-reads the balance for the order as it now is, inside the queued
   * operation, so the picker's ticks are the server's by the time the
   * operation (and `busy`) ends. Best effort: a failure here leaves the
   * ordinary query to ask again.
   */
  const refreshBalance = useCallback(
    async (updated: GetOrderDto | null) => {
      if (!updated?.id || !updated.currency || !credentialHeld) return;
      try {
        // Never joined to a read already in flight: one started before this
        // change would hand back the ticks from before it (review I2).
        await freshQuery(queryClient, balanceKey(updated), () =>
          fetchBalance(updated.currency as string, updated.id),
        );
      } catch {
        // The query on screen asks again on its own.
      }
    },
    [queryClient, balanceKey, fetchBalance, credentialHeld],
  );
  // The whole balance, minimums not applied — what an apply's outcome is
  // judged against (`creditApplyProblem`: held-back credit still means the
  // customer HAS credit, it just cannot go on this order).
  const totalBalanceCents =
    balanceQuery.isSuccess && typeof balanceQuery.data?.balanceCents === "number"
      ? balanceQuery.data.balanceCents
      : null;
  // What the box offers and calls "left": only credit usable on this order.
  const balanceCents = usableNowCents(
    totalBalanceCents,
    balanceQuery.data?.lockedByMinimum,
  );
  const minimumLines = useMemo(
    () =>
      balanceQuery.isSuccess
        ? creditMinimumLines(
            balanceQuery.data?.lockedByMinimum,
            balanceQuery.data?.orderGoodsCents,
          )
        : [],
    [balanceQuery.isSuccess, balanceQuery.data],
  );
  const lots = balanceQuery.isSuccess ? (balanceQuery.data?.lots ?? null) : null;
  const selectionMode = balanceQuery.isSuccess
    ? (balanceQuery.data?.selectionMode ?? null)
    : null;
  // Read when a queued operation RUNS, like `balanceRef` below.
  const lotsRef = useRef({ lots, selectionMode });
  useEffect(() => {
    lotsRef.current = { lots, selectionMode };
  }, [lots, selectionMode]);
  // Asked and refused, as distinct from not answered yet.
  const balanceFailed = balanceQuery.isError;
  const accountUnverified =
    balanceQuery.isError &&
    creditProblemFromApiError(balanceQuery.error) === "account-unverified";

  // The balance an apply's outcome is judged against, read when the apply
  // RUNS: a queued apply (after a redemption, say) must see the balance the
  // redemption just re-read, not the one from the render that queued it.
  const balanceRef = useRef(totalBalanceCents);
  useEffect(() => {
    balanceRef.current = totalBalanceCents;
  }, [totalBalanceCents]);

  /**
   * Whether a credit operation is running or queued for this order, or its
   * payment has begun — shared by every mount, so a delivery step mounted
   * again (Back, Forward) while a redemption is still running waits for it
   * too instead of offering a checkbox that would race it.
   */
  const activeOrderId = order?.id || mainOrderId;
  const busy = useSyncExternalStore(
    creditOperations.subscribe,
    () =>
      activeOrderId
        ? creditOperations.isBusy(activeOrderId) || creditOperations.isPaying(activeOrderId)
        : false,
    () => false,
  );

  /**
   * Apply the order's credit NOW. Only ever called from inside a queued
   * operation (`credit-operations.ts`), never directly from a click.
   *
   * @returns whether credit is on the order the way the customer asked; a
   *          refusal, or an apply that moved nothing, is a failure — which is
   *          what holds a payment pressed meanwhile.
   */
  const applyNow = useCallback(
    async (orderId: string, lotIds?: string[]): Promise<boolean> => {
      setProblem(null);
      trackStep("credit-submitted", { orderId });

      // Read before the try: the catch needs to know which token was refused.
      const storedToken = readUsableCreditToken(audience);
      try {
        // Omitted when the caller is signed in as the credit owner — the server
        // then proves ownership from the bearer token instead. A guest must
        // present the exchanged claim token, and a lapsed one reads as null here,
        // which the server refuses with CREDIT_PROOF_REQUIRED and this hook
        // turns back into "open the link in your email again".
        const payload: UpdateOrderCreditDto = {
          ...(storedToken ? { creditToken: storedToken } : {}),
          // The picker's choice; absent, the server picks (automatic mode).
          ...(lotIds ? { lotIds } : {}),
        };

        await orderApi.orderControllerApplyCreditToOrder(
          orderId,
          payload,
          guestToken ?? undefined,
          undefined,
        );
        const updated = await refreshOrder(orderId);
        await refreshBalance(updated);
        // A choice of lots that pays nothing right now (only lots below their
        // minimum, say) is what the customer asked for, not a failure.
        if (lotIds) return true;
        // An apply that moved nothing is a SUCCESS on the wire: `redeem` returns
        // 0 when the address has no usable lot rather than refusing. Left
        // unsaid, a customer who pressed a button about their money watches
        // nothing happen at all — and since the offer is now shown to any
        // signed-in customer, that is the common case, not the edge one.
        const applied = appliedCreditCents(updated);
        if (applied === 0) {
          const outcome = creditApplyProblem({
            appliedCents: applied,
            orderTotalCents: preCreditTotalCents(updated),
            balanceCents: balanceRef.current,
          });
          if (outcome) {
            setProblem(outcome);
            trackLoss("credit-rejected", { orderId, reason: outcome });
            return false;
          }
        }
        return true;
      } catch (error: unknown) {
        const rejected = creditProblemFromApiError(error);
        setProblem(rejected);
        // A refused credit is a customer who was turned away from money they
        // were told they had — the same bar `coupon-rejected` already clears.
        // `reason` is the stable enum, not the sentence: the prose is
        // translated and would both explode cardinality and fall past the
        // 40-character tag cutoff.
        trackLoss("credit-rejected", {
          orderId,
          reason: rejected,
          status: (error as { response?: { status?: number } })?.response?.status,
          code: (error as { response?: { data?: { code?: string } } })?.response
            ?.data?.code,
        });
        // An expired or invalid token, or another address's, will be refused
        // on every retry. Dropping it turns the section back into the "your
        // link has expired" state rather than a button that fails identically
        // however many times it is pressed — and lets a signed-in customer's
        // next request fall back to their own account. (Spent or lapsed is
        // about the money, not the token, and keeps it: `creditTokenRefused`.)
        if (storedToken && creditTokenRefused(rejected)) {
          // clearStoredCreditToken notifies the store, so every mounted section
          // drops the offer together.
          clearStoredCreditToken(storedToken);
        }
        return false;
      }
    },
    [guestToken, refreshOrder, refreshBalance, audience],
  );

  const removeNow = useCallback(
    async (orderId: string): Promise<boolean> => {
      setProblem(null);
      try {
        // No token: the backend deliberately asks for no proof to release
        // credit, so a customer whose claim token lapsed is not stranded with
        // credit on a cart they cannot change.
        await orderApi.orderControllerRemoveCreditFromOrder(
          orderId,
          guestToken ?? undefined,
          undefined,
        );
        await refreshBalance(await refreshOrder(orderId));
        return true;
      } catch (error: unknown) {
        setProblem(creditProblemFromApiError(error));
        return false;
      }
    },
    [guestToken, refreshOrder, refreshBalance],
  );

  const choose = useCallback(
    async (use: boolean) => {
      const orderId = order?.id || mainOrderId;
      if (!orderId || creditOperations.isPaying(orderId)) return;
      // Remembered at once, so every render from here on reads the new
      // choice; the operation itself may still wait its turn.
      setOptedOut(orderId, !use);
      await creditOperations.enqueue(
        orderId,
        () => (use ? applyNow(orderId) : removeNow(orderId)),
        { choice: true },
      );
    },
    [order?.id, mainOrderId, applyNow, removeNow],
  );

  const chooseLots = useCallback(
    async (lotIds: string[]) => {
      const orderId = order?.id || mainOrderId;
      if (!orderId || creditOperations.isPaying(orderId)) return;
      // "None" is the same decision as unticking "use my credit" was: kept,
      // so nothing ticks it back automatically.
      setOptedOut(orderId, lotIds.length === 0);
      await creditOperations.enqueue(
        orderId,
        // None goes through remove, which needs no proof: a customer whose
        // claim token lapsed can still take their credit off.
        () => (lotIds.length === 0 ? removeNow(orderId) : applyNow(orderId, lotIds)),
        { choice: true },
      );
    },
    [order?.id, mainOrderId, applyNow, removeNow],
  );

  const autoApply = useCallback(async () => {
    const orderId = order?.id || mainOrderId;
    if (!orderId) return;
    await requestTopUp(creditOperations, orderId, () => applyNow(orderId));
  }, [order?.id, mainOrderId, applyNow]);

  const redeemCode = useCallback(
    async (code: string): Promise<boolean> => {
      const orderId = order?.id || mainOrderId;
      if (!orderId) return false;

      // Malformed input never reaches the throttled endpoint: the backend
      // gives the identical GIFT_CARD_NOT_FOUND for malformed and unknown
      // ("one answer for both"), so refusing it here costs nothing and
      // spares one of the five attempts a minute a guest gets.
      const normalized = normalizeGiftCardCode(code);
      if (!normalized) {
        setRedeemErrorCode("GIFT_CARD_NOT_FOUND");
        return false;
      }

      setRedeemErrorCode(null);
      // The lots listed before the code, so the apply after it can tell the
      // new card apart (`selectionAfterRedeem`).
      const knownBefore = (lotsRef.current.lots ?? []).map((l) => l.id);
      const transaction = redeemAndApply(creditOperations, orderId, {
        redeem: async () => {
          try {
            // With the token held now, so the answer covers the cards
            // entered before this one too (see `redeemCodeDto`).
            const { data } = await creditApi.giftCardRedeemControllerRedeemCode(
              redeemCodeDto(normalized, orderId, audience),
              guestToken ?? undefined,
            );
            // Stored as the server built it; never over a whole-address token
            // for the same email with a narrower one.
            storeRedeemedCreditToken(data.creditToken);
            // Keyed on `hasClaimToken`, which the token write above changes for
            // a guest — but a signed-in customer's key does not move, and the
            // new lot must be seen NOW, not on the next unrelated re-key.
            await queryClient.invalidateQueries({
              predicate: (q) => q.queryKey[0] === "credit-balance",
            });
            return true;
          } catch (error: unknown) {
            setRedeemErrorCode(
              giftCardRedeemErrorCode(error) ?? "GIFT_CARD_REDEEM_UNAVAILABLE",
            );
            return false;
          }
        },
        // A credit error from before (an apply that failed on too little
        // balance, say) is about money this code may have just changed.
        onRedeemed: () => setProblem(null),
        optIn: () => {
          // The customer redeeming a code IS them opting back in, even if they
          // had earlier unticked "use my credit" on this order — leaving the
          // opt-out would silently strand the money they just typed a code in
          // for. And the apply right after this is this order's auto-apply
          // for the new balance, so the effect must not fire a second one.
          setOptedOut(orderId, false);
          markAutoApplied(orderId);
        },
        apply: async () => {
          // Automatic mode applies as it always did. A manual choice (or a
          // mode not known yet) re-reads the lots — the card is on them now —
          // and applies the choice plus the card. A read that fails twice
          // applies nothing and says so: applying without lotIds would turn
          // the customer's choice back into automatic (review I3).
          const plan = await applyAfterRedeem(
            lotsRef.current.selectionMode,
            knownBefore,
            () => fetchBalance(orderCurrency, orderId),
          );
          if (plan.kind === "keep") {
            setProblem("busy");
            return false;
          }
          return applyNow(orderId, plan.lotIds);
        },
      });
      // Refused: payment for this order has already begun.
      if (!transaction) return false;
      return transaction.redeemed;
    },
    [order?.id, mainOrderId, guestToken, queryClient, applyNow, audience, fetchBalance, orderCurrency],
  );

  return {
    // Read from the SERVER order, never from local state: `refetchOnMount:
    // "always"` discards anything held locally on the next mount, and this is
    // what makes the applied-credit chip survive a navigation, exactly as
    // `order.couponUsed` does for the coupon.
    creditAppliedCents: appliedCreditCents(order),
    hasCredit: hasCreditApplied(order),
    hasClaimToken,
    tokenAwaitingEmail,
    tokenScope,
    isSignedIn,
    currency: order?.currency || "",
    balanceCents,
    minimumLines,
    lots,
    selectionMode,
    balanceFailed,
    accountUnverified,
    orderId: order?.id ?? null,
    orderEditable: creditEditable(order),
    paymentStarted: creditLockedByPayment(order),
    busy,
    problem,
    clearProblem: useCallback(() => setProblem(null), []),
    choose,
    chooseLots,
    autoApply,
    redeemCode,
    redeemErrorCode,
    clearRedeemError: useCallback(() => setRedeemErrorCode(null), []),
  };
}
